import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import MarkdownRenderer from 'ui-component/system/MarkdownRenderer';
import type { SmartTemplateAiPayload } from './smartTemplateAiTypes';

export type TemplateAIGeneratedPreviewDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  mode: 'smart' | 'legacy';
  smartPayload?: SmartTemplateAiPayload | null;
  legacyText?: string;
};

const TemplateAIGeneratedPreviewDialog: React.FC<TemplateAIGeneratedPreviewDialogProps> = ({
  open,
  onClose,
  onConfirm,
  mode,
  smartPayload,
  legacyText,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>תצוגה מקדימה — מחולל תבניות</DialogTitle>
      <DialogContent dividers>
        {mode === 'smart' && smartPayload ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {smartPayload.marketingOptOutAppended && (
              <Alert severity="info" sx={{ mb: 0.5 }}>
                כפתור הסרה נוסף אוטומטית בסוף הרשימה בהתאם למדיניות פייסבוק.
              </Alert>
            )}
            <Typography variant="subtitle2" color="text.secondary">
              שם באנגלית (template_name_en)
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {smartPayload.template_name_en}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              שם בעברית (template_name_he)
            </Typography>
            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
              {smartPayload.template_name_he}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              קטגוריה
            </Typography>
            <Typography variant="body2">{smartPayload.category ?? 'MARKETING'}</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary">
              גוף ההודעה (body_text)
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {smartPayload.body_text}
            </Typography>
            {/\{\{\d+\}\}/.test(smartPayload.body_text) && (
              <Alert severity="success" variant="outlined" sx={{ py: 0.5 }}>
                דוגמאות לערכי משתנים בגוף (ובקישור דינמי אם קיים) יוזרקו אוטומטית לטופס — לא נדרש מילוי ידני.
              </Alert>
            )}
            {smartPayload.body_parameter_examples &&
              Object.keys(smartPayload.body_parameter_examples).length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                    דוגמאות לפרמטרים בגוף (יוזרקו אוטומטית לטופס)
                  </Typography>
                  {Object.entries(smartPayload.body_parameter_examples).map(([k, v]) => (
                    <Typography key={k} variant="body2">
                      {'{{'}
                      {k}
                      {'}}'}: {v}
                    </Typography>
                  ))}
                </>
              )}
            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
              כפתורים (כולל כפתור הסרה אם נוסף אוטומטית)
            </Typography>
            {smartPayload.buttons.map((b, i) => (
              <Typography key={i} variant="body2">
                {b.type}: {b.text}
                {b.type === 'URL' && b.url ? ` — ${b.url}` : ''}
                {b.type === 'URL' && b.url_variable_example ? ` · דוגמה לקישור דינמי: ${b.url_variable_example}` : ''}
              </Typography>
            ))}
          </Box>
        ) : (
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              תשובת ה-AI (Legacy)
            </Typography>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 1,
                bgcolor: 'action.hover',
                maxHeight: 360,
                overflow: 'auto',
              }}
            >
              <MarkdownRenderer markdownText={legacyText || ''} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              באישור, הטקסט יוחל על שדה גוף ההודעה בלבד (שאר רכיבי התבנית נשמרים).
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          ביטול
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirm}>
          אישור והחלה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateAIGeneratedPreviewDialog;
